import { formatCardNumber, formatPhoneNumber } from '@/features/requisites'
import {
  BANK_CONFIG,
  BankType,
  bankTypeToTraderBankMapping,
  NewWithdrawModel,
  RequisitType,
  requisitTypeToTraderMethodMapping,
} from '@/features/withdraws'
import { BaseDialog, BaseInput, BaseSelect } from '@/shared/components'
import { generateGUID } from '@/shared/lib/guid'
import { useCallback, useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { AllBanksTypes, AllMethodsTypes } from 'syncra-storybook/shared/utils'

type FormData = {
  merchantId: string
  bank: BankType | ''
  requisitType: RequisitType | ''
  requisitValue: string
  recipientFullName: string
  currency: string
  amount: string
  requestId: string
  bankToName: AllBanksTypes
  methodToName: AllMethodsTypes
  countryToName: 'ru'
  callbackUrl: ''
}

type Props = {
  modalVisible: boolean
  setModalVisible: (value: boolean) => void
  onSubmit: (data: NewWithdrawModel) => void
  isLoading?: boolean
}

// Валидация карты (простая проверка на 16 цифр)
const validateCard = (value: string): string | undefined => {
  const cleaned = value.replace(/\D/g, '')
  if (cleaned.length !== 16) {
    return 'Номер карты должен содержать 16 цифр'
  }
  return undefined
}

// Валидация телефона
const validatePhone = (value: string): string | undefined => {
  const cleaned = value.replace(/\D/g, '')
  if (cleaned.length !== 11) {
    return 'Номер телефона должен содержать 11 цифр'
  }
  if (!cleaned.startsWith('7') && !cleaned.startsWith('8')) {
    return 'Номер должен начинаться с 7 или 8'
  }
  return undefined
}

export const CreateWithdrawDialog = ({
  modalVisible,
  setModalVisible,
  onSubmit,
  isLoading,
}: Props) => {
  const {
    register,
    handleSubmit,
    control,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: {
      bank: '',
      requisitType: '',
      requisitValue: '',
      recipientFullName: '',
      bankToName: AllBanksTypes.TBank,
      methodToName: AllMethodsTypes.Card,
      countryToName: 'ru',
      currency: '643',
      amount: '',
      requestId: '',
      callbackUrl: '',
    },
  })

  const [formattedRequisit, setFormattedRequisit] = useState('')
  const [requisitError, setRequisitError] = useState<string | undefined>(undefined)

  const watchedBank = watch('bank')
  const watchedRequisitType = watch('requisitType')

  // Создание опций для банков
  const bankOptions = Object.entries(BANK_CONFIG).map(([key, config]) => ({
    value: key,
    label: config.name,
  }))

  // Автоматическое обновление bankToName при изменении bank
  useEffect(() => {
    if (watchedBank) {
      const traderBankType = bankTypeToTraderBankMapping[watchedBank as BankType]
      setValue('bankToName', traderBankType)
    }
  }, [watchedBank, setValue])

  // Автоматическое обновление methodToName при изменении requisitType
  useEffect(() => {
    if (watchedRequisitType) {
      const traderMethodType =
        requisitTypeToTraderMethodMapping[watchedRequisitType as RequisitType]
      setValue('methodToName', traderMethodType)
    }
  }, [watchedRequisitType, setValue])

  // Сброс типа реквизита при смене банка
  useEffect(() => {
    if (watchedBank) {
      setValue('requisitType', '')
      setValue('requisitValue', '')
      setFormattedRequisit('')
      setRequisitError(undefined)
    }
  }, [watchedBank, setValue])

  // Получение доступных типов реквизитов для выбранного банка
  const getAvailableRequisitTypes = () => {
    if (!watchedBank) return []
    return BANK_CONFIG[watchedBank as BankType].availableTypes
  }

  // Создание опций для типов реквизитов
  const requisitTypeOptions = getAvailableRequisitTypes().map((type) => ({
    value: type,
    label: type === RequisitType.Card ? 'Карта' : 'СБП',
  }))

  // Обработчик изменения реквизита
  const handleRequisitChange = useCallback(
    (value: string) => {
      if (!watchedRequisitType) return

      let formatted = value
      let cleaned = value
      let error: string | undefined = undefined

      if (watchedRequisitType === RequisitType.Card) {
        const result = formatCardNumber(value)
        formatted = result.formatted
        cleaned = result.cleaned
        error = validateCard(cleaned)
      } else if (watchedRequisitType === RequisitType.Phone) {
        const result = formatPhoneNumber(value)
        formatted = result.formatted
        cleaned = result.cleaned
        error = validatePhone(cleaned)
      }

      setFormattedRequisit(formatted)
      setRequisitError(error)
      setValue('requisitValue', cleaned)
    },
    [watchedRequisitType, setValue]
  )

  const handleClose = () => {
    reset()
    setFormattedRequisit('')
    setRequisitError(undefined)
    setModalVisible(false)
  }

  const handleFormSubmit = (data: FormData) => {
    // Проверяем что банк выбран
    if (!data.bank) return
    if (!data.requisitType) return

    // Получаем тип метода на основе выбранного банка и типа реквизита
    const bankConfig = BANK_CONFIG[data.bank as BankType]
    const requisitTypeTo = bankConfig.methodMapping[data.requisitType as RequisitType]

    // Генерируем GUID если requestId пустой
    const requestId = data.requestId.trim() || generateGUID()

    const requestData: NewWithdrawModel = {
      amountTo: parseFloat(data.amount) || 0,
      currencyTo: parseInt(data.currency),
      requisitTypeTo,
      requisitValueTo: data.requisitValue,
      recipientFullName: data.recipientFullName,
      requestId,
      bankToName: data.bankToName,
      methodToName: data.methodToName,
      countryToName: data.countryToName,
      callbackUrl: data.callbackUrl,
    }

    onSubmit(requestData)
  }

  return (
    <BaseDialog
      isVisible={modalVisible}
      onClose={handleClose}
      onAction={handleSubmit(handleFormSubmit)}
      title={'Создание заявки'}
      actionButtonName={'Создать'}
      isLoading={isLoading}
    >
      <div className="max-h-[600px] overflow-y-auto scrollbar-hide">
        <form onSubmit={handleSubmit(handleFormSubmit)} className="flex flex-col gap-y-4 p-0.5">
          <BaseInput
            label={'ID Мерчанта'}
            placeholder="Оставьте пустым для автогенерации"
            {...register('requestId')}
            variant="secondary"
          />

          <div className="gap-4 grid grid-cols-2">
            <Controller
              name="bank"
              control={control}
              rules={{ required: 'Необходимо выбрать банк' }}
              render={({ field, fieldState }) => (
                <BaseSelect
                  {...field}
                  label="Банк"
                  placeholder="Выберите"
                  options={bankOptions}
                  error={fieldState.error?.message}
                />
              )}
            />

            <Controller
              name="requisitType"
              control={control}
              rules={{ required: 'Выберите тип реквизита' }}
              render={({ field, fieldState }) => (
                <BaseSelect
                  {...field}
                  label="Тип реквизита"
                  placeholder="Выберите"
                  options={requisitTypeOptions}
                  error={fieldState.error?.message}
                  disabled={!watchedBank}
                  key={requisitTypeOptions.length} // Перерендер при изменении опций
                />
              )}
            />
          </div>

          <BaseInput
            label={'Реквизит'}
            placeholder={
              watchedRequisitType === RequisitType.Card
                ? '1234 5678 9012 3456'
                : watchedRequisitType === RequisitType.Phone
                  ? '+7 (999) 123-45-67'
                  : 'Ввести'
            }
            value={formattedRequisit}
            onChange={(e) => handleRequisitChange(e.target.value)}
            variant="secondary"
            error={requisitError}
            disabled={!watchedRequisitType}
          />

          <BaseInput
            label={'Фамилия и Имя получателя'}
            placeholder="Ввести"
            {...register('recipientFullName')}
            variant="secondary"
            error={errors.recipientFullName?.message}
          />

          <BaseInput
            label={'Сумма'}
            placeholder="Ввести"
            {...register('amount', {
              required: 'Поле обязательно для заполнения',
              pattern: {
                value: /^\d+(\.\d{1,2})?$/,
                message: 'Введите корректную сумму',
              },
            })}
            variant="secondary"
            error={errors.amount?.message}
          />

          <BaseInput
            label={'Callback url'}
            placeholder="Ввести"
            {...register('callbackUrl')}
            variant="secondary"
            error={errors.callbackUrl?.message}
          />

          {/* Скрытое поле для валюты */}
          <input type="hidden" {...register('currency')} value="643" />
          <input type="hidden" {...register('currency')} value="643" />
          <input type="hidden" {...register('bankToName')} />
          <input type="hidden" {...register('methodToName')} />
        </form>
      </div>
    </BaseDialog>
  )
}
